import { Cancel as CancelIcon, PersonAdd as RequestIcon } from "@mui/icons-material";
import { Chip, CircularProgress, Menu, MenuItem } from "@mui/material";
import { type MouseEvent, useState } from "react";

import { useMembershipStatus } from "~ui/hooks/useMembershipStatus";

interface MembershipChipProps {
  isOwner: boolean;
  isFacilitator?: boolean;
  isMember: boolean;
  isPending: boolean;
  isPublic?: boolean;
  onJoin?: () => Promise<void>;
  onLeave?: () => Promise<void>;
  onCancel?: () => Promise<void>;
}

export const MembershipChip = ({
  isOwner,
  isFacilitator = false,
  isMember,
  isPending,
  isPublic,
  onJoin,
  onLeave,
  onCancel,
}: MembershipChipProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const open = Boolean(anchorEl);

  const status = useMembershipStatus({
    userIsFacilitator: isFacilitator,
    userIsMember: isMember,
    userIsOwner: isOwner,
    userIsPending: isPending,
  });

  const handleClick = async (event: MouseEvent<HTMLDivElement>) => {
    // If we're not a member and not pending, directly trigger join
    if (!isMember && !isPending && onJoin) {
      try {
        setIsLoading(true);
        await onJoin();
      } finally {
        setIsLoading(false);
      }
      return;
    }
    // Otherwise show menu for leave/cancel options
    if ((isMember || isPending) && (onLeave || onCancel)) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLeave = async () => {
    if (onLeave) {
      try {
        setIsLoading(true);
        handleClose();
        await onLeave();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelRequest = async () => {
    if (onCancel) {
      try {
        setIsLoading(true);
        handleClose();
        await onCancel();
      } finally {
        setIsLoading(false);
      }
    }
  };

  // If no status and no actions available, don't render anything
  if (!status && !onJoin) {
    return null;
  }

  // If no status but join action is available, show "Request to Join" chip
  if (!status && onJoin) {
    return (
      <Chip
        size="small"
        icon={isLoading ? <CircularProgress size={16} /> : <RequestIcon fontSize="small" />}
        label={isLoading ? "Joining..." : isPublic ? "Join" : "Request to Join"}
        color="primary"
        onClick={handleClick}
        sx={{ cursor: isLoading ? "default" : "pointer" }}
        disabled={isLoading}
      />
    );
  }

  // For existing members/pending requests, show status chip with actions
  if (!status) {
    return null;
  }

  return (
    <>
      <Chip
        size="small"
        icon={isLoading ? <CircularProgress size={16} /> : <status.Icon fontSize="small" />}
        label={isLoading ? (isPending ? "Cancelling..." : "Leaving...") : status.label}
        color={status.color}
        onClick={handleClick}
        sx={{
          cursor: isLoading ? "default" : (isMember || isPending) && (onLeave || onCancel) ? "pointer" : "default",
        }}
        disabled={isLoading}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        {isMember && onLeave && (
          <MenuItem onClick={handleLeave} sx={{ color: "error.main" }}>
            Leave
          </MenuItem>
        )}
        {isPending && onCancel && (
          <MenuItem onClick={handleCancelRequest}>
            <CancelIcon fontSize="small" sx={{ mr: 1 }} />
            Cancel Request
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
